import { getStaffSessionToken } from '../modules/auth/staffSession';
import { useEffect, useState } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { Camera } from '../modules/cameras/api/types';
import { Link } from 'react-router-dom';
import { PaperIcon } from '../assets/icons/paper';
import { ChangeIcon } from '../assets/icons/change';
import { ArrowRightIcon } from '../assets/icons/arrowRight';
import { PencilIcon } from '../assets/icons/pencil';
import { LivestreamIcon } from '../assets/icons/livestream';
import {
  useLazyCreatePortalSessionQuery,
  useCancelPauseSubscriptionMutation,
  useDeactivatePauseMutation,
  useTerminateSimMutation,
  useRefundPreviewMutation,
  useInitiateSimTransferMutation,
  useConfirmSimTransferMutation,
  useAddSimsToPlanMutation,
  useResolveActivationCodeMutation,
  useGetRouterSnapshotMutation,
  useGetCachedRouterSnapshotMutation,
  useLazyGetCameraSnapshotQuery,
  useRefreshCameraSnapshotMutation
} from '../modules/subscription/api/subscriptionApi';
import { toast } from 'react-toastify';
import { useModal } from '../hooks/useModal';
import { Button } from './Button';
import { useSelector } from 'react-redux';
import { authSelector } from '../modules/auth/state/authSlice';
import { getAddSimsQuote, normaliseSimQuantity } from '../modules/subscription/addSimsPricing';

// Shopify cart permalink for the £0 replacement SIM product.
// To enable pre-filled cart + checkout, set REPLACEMENT_SIM_VARIANT_ID below to
// the Shopify product *variant* ID (Shopify admin → Products → Farmstream
// Streaming SIM → variant → URL contains "variants/<id>"). When set, the link
// opens Shopify's cart with the product already added, the original ICCID in
// the order note, and the customer's email pre-filled at checkout. When empty
// it falls back to the product page.
const REPLACEMENT_SIM_VARIANT_ID = '40398256832558';
const REPLACEMENT_SIM_PRODUCT_URL =
  'https://www.farmstream.co.uk/products/farmstream-streaming-sim';

const buildReplacementSimUrl = (iccid: string, email: string | null): string => {
  if (!REPLACEMENT_SIM_VARIANT_ID) {
    return REPLACEMENT_SIM_PRODUCT_URL;
  }
  const params = new URLSearchParams();
  params.set('note', `Replacement for ${iccid}`);
  if (email) {
    params.set('checkout[email]', email);
  }
  return `https://www.farmstream.co.uk/cart/${REPLACEMENT_SIM_VARIANT_ID}:1?${params.toString()}`;
};

const getEmailFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return typeof decoded?.email === 'string' ? decoded.email : null;
  } catch {
    return null;
  }
};

// Shape returned by POST /admin/refund-preview (amounts in pence)
type RefundPreviewData = {
  refund_available: boolean;
  currency: string | null;
  last_payment_pence: number;
  already_refunded_pence: number;
  max_refund_pence: number;
  prorata_pence: number | null;
  days_total: number | null;
  days_remaining: number | null;
  payment_date: number | null;
};

const formatMoney = (pence: number, currency: string | null): string =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: (currency || 'gbp').toUpperCase()
  }).format(pence / 100);

type CameraCardProps = {
  camera: Camera;
  onEditCamera: (camera: Camera) => void;
  onOpenPauseModal: (stripeId: string) => void;
  onRefreshData?: () => void; // Add this prop
};

export const CameraCard = ({ camera, onEditCamera, onOpenPauseModal, onRefreshData }: CameraCardProps) => {
  const { colors } = useTheme();
  const { isAdmin, token } = useSelector(authSelector);
  const customerEmail = getEmailFromToken(token);
  const replacementSimUrl = buildReplacementSimUrl(camera.SIM_ICCID, customerEmail);
  const [createPortalSession] = useLazyCreatePortalSessionQuery();
  const [cancelPauseSubscription, { isLoading: isCancelingPause }] = useCancelPauseSubscriptionMutation();
  const [deactivatePause, { isLoading: isResumingPlan }] = useDeactivatePauseMutation();
  const [terminateSim, { isLoading: isTerminating }] = useTerminateSimMutation();
  const [initiateSimTransfer, { isLoading: isInitiatingTransfer }] = useInitiateSimTransferMutation();
  const [confirmSimTransfer, { isLoading: isConfirmingTransfer }] = useConfirmSimTransferMutation();
  const [addSimsToPlan, { isLoading: isAddingSims }] = useAddSimsToPlanMutation();
  const [resolveActivationCode, { isLoading: isResolvingCode }] = useResolveActivationCodeMutation();
  const [getRouterSnapshot, { isLoading: snapshotLoading }] = useGetRouterSnapshotMutation();
  const [getCachedRouterSnapshot] = useGetCachedRouterSnapshotMutation();
  const [getCameraSnapshot] = useLazyGetCameraSnapshotQuery();
  const [refreshCameraSnapshot, { isLoading: customerRefreshLoading }] =
    useRefreshCameraSnapshotMutation();
  const status = camera.SubStatus;
  const simStatus = camera.SimStatus;
  const labelDashboard = camera.labelDashboard; //so the label tags can be independtly renamed by backend

  // True when the customer hasn't customised the camera nickname yet
  // (backend default is the literal string "set a nickname").
  const hasDefaultNickname = (camera.nicknameInDatabase ?? '')
    .trim()
    .toLowerCase()
    .replace(/['"]/g, '') === 'set a nickname';

  // Helper function to check if it's a Heavy Streaming plan
  const isHeavyStreamingPlan = () => {
    const productName = camera.stripeSubscription?.productName || '';
    return productName.toLowerCase().includes('heavy');
  };

  // Solar plans don't work with the router-snapshot lambda (no LAN reachable router).
  const isSolarPlan = () => {
    const productName = camera.stripeSubscription?.productName || '';
    return productName.toLowerCase().includes('solar');
  };

  // Snapshot state (non-solar). Customers see cached snapshots maintained by
  // the monthly router_snapshot_refresh Lambda; admins can also trigger a
  // fresh capture via the refresh button.
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotKey, setSnapshotKey] = useState(0);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [snapshotTakenAt, setSnapshotTakenAt] = useState<string | null>(null);

  // Auto-load any previously captured snapshot for this SIM when the card mounts.
  // Admins get the admin endpoint (which can also trigger a fresh capture if
  // none is cached); customers get the JWT-protected cached-only endpoint.
  useEffect(() => {
    if (isSolarPlan()) return;
    let cancelled = false;
    if (isAdmin) {
      const staffSession = getStaffSessionToken();
      if (!staffSession) return;
      (async () => {
        try {
          const res = await getCachedRouterSnapshot({
            iccid: camera.SIM_ICCID,

          }).unwrap();
          if (cancelled) return;
          if (res?.snapshot_url) {
            setSnapshotUrl(res.snapshot_url);
            setSnapshotTakenAt(res.snapshot_taken_at || null);
            setSnapshotKey((k) => k + 1);
            return;
          }
        } catch {
          // 404 (no cached) falls through to a fresh capture below.
        }
        if (cancelled) return;
        try {
          const fresh = await getRouterSnapshot({
            iccid: camera.SIM_ICCID,

          }).unwrap();
          if (cancelled) return;
          if (fresh?.snapshot_url) {
            setSnapshotUrl(fresh.snapshot_url);
            setSnapshotTakenAt(new Date().toISOString());
            setSnapshotKey((k) => k + 1);
          }
        } catch {
          // Silent: camera may be offline.
        }
      })();
    } else {
      (async () => {
        try {
          const res = await getCameraSnapshot(camera.SIM_ICCID).unwrap();
          if (cancelled) return;
          if (res?.snapshot_url) {
            setSnapshotUrl(res.snapshot_url);
            setSnapshotTakenAt(res.snapshot_taken_at || null);
            setSnapshotKey((k) => k + 1);
          }
        } catch {
          // No cached snapshot yet; render nothing.
        }
      })();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.SIM_ICCID, isAdmin]);

  const handleFetchSnapshot = async () => {
    setSnapshotError(null);
    if (isAdmin) {
      const staffSession = getStaffSessionToken();
      if (!staffSession) {
        toast('Admin session expired, please login again', { type: 'error' });
        return;
      }
      try {
        const res = await getRouterSnapshot({
          iccid: camera.SIM_ICCID,

        }).unwrap();
        if (res?.success === false) {
          setSnapshotError(res?.message || 'snapshot failed');
          return;
        }
        if (!res?.snapshot_url) throw new Error(res?.message || 'no snapshot returned');
        setSnapshotUrl(res.snapshot_url);
        setSnapshotTakenAt(new Date().toISOString());
        setSnapshotKey((k) => k + 1);
      } catch (err: any) {
        setSnapshotError(err?.data?.message || err?.message || 'snapshot failed');
      }
      return;
    }
    try {
      const res = await refreshCameraSnapshot(camera.SIM_ICCID).unwrap();
      if (res?.success === false) {
        setSnapshotError(res?.message || 'snapshot failed');
        return;
      }
      if (!res?.snapshot_url) throw new Error(res?.message || 'no snapshot returned');
      setSnapshotUrl(res.snapshot_url);
      setSnapshotTakenAt(new Date().toISOString());
      setSnapshotKey((k) => k + 1);
    } catch (err: any) {
      setSnapshotError(err?.data?.message || err?.message || 'snapshot failed');
    }
  };

  // Helper function to format subscription plan name
  const getSubscriptionPlanDisplay = () => {
    const productName = camera.stripeSubscription?.productName || '';
    const interval = camera.stripeSubscription?.interval || 'month';

    if (!productName) {
      return 'No Plan';
    }

    // Extract plan type from product name (Light, Standard, Heavy)
    let planType = '';
    const lowerProductName = productName.toLowerCase();

    if (lowerProductName.includes('light')) {
      planType = 'Light';
    } else if (lowerProductName.includes('standard')) {
      planType = 'Standard';
    } else if (lowerProductName.includes('heavy')) {
      planType = 'Heavy';
    } else {
      // Fallback to original product name if no match, remove "Streaming" if present
      planType = productName.replace(/\s*(Streaming|streaming)\s*/g, '').trim() || 'Unknown';
    }

    // Always display as "Streaming" instead of interval
    return `${planType} Streaming`;
  };

  //the state where the subscription is active and the sim is active
  const isActive = status === 'active';

  //the state where the subscription is paused and the sim is paused
  //should be a route to resume the subscription
  const isPaused = status === 'paused';

  //the state where the subscription is overdue and the sim is active
  //should be a route to pay the overdue invoice - stright to stripe hosted page (using exising route)
  const isOverdue = status === 'past_due' || status === 'overdue';

  //the state where the subscription is canceled and the sim is terminated.
  //only invoices are available to view
  const isCanceled = status === 'canceled' && simStatus === 'terminated';

  //the sate where there is not an active subscription but the sim is still active
  // customer should be redirected to pricing table to subscribe to a subscription using client secret
  const pendingCancellation =
    (simStatus === 'active' || simStatus === 'paused') && (status === 'canceled' || status === 'incomplete_expired');

  const isTerminatedSim = simStatus === 'terminated';

  // State for when subscription payment failed/expired - no plan details to show
  const isPaymentFailed = status === 'incomplete_expired';

  // Add this new state check after your existing state declarations
  const isPauseScheduled = status === 'pause scheduled';

  // Calculate pause and resume dates for paused or pause scheduled subscriptions
  let resumeDate = '';
  let pauseDate = '';
  let scheduledResumeDate = '';

  // For paused subscriptions - try first from statusData if available, fallback to stripeSubscription
  if (isPaused) {
    if (camera.statusData?.subscriptionStatus?.pause?.end_date) {
      resumeDate = formatDateToLocale(camera.statusData.subscriptionStatus.pause.end_date);
    } else if (camera.stripeSubscription?.pause_collection?.resumes_at) {
      resumeDate = new Date(
        camera.stripeSubscription.pause_collection.resumes_at * 1000
      ).toLocaleDateString();
    }
  }

  // For scheduled pauses - get dates from statusData.subscriptionStatus.pause
  if (isPauseScheduled) {
    if (camera.statusData?.subscriptionStatus?.pause) {
      pauseDate = formatDateToLocale(camera.statusData.subscriptionStatus.pause.start_date);
      scheduledResumeDate = formatDateToLocale(camera.statusData.subscriptionStatus.pause.end_date);
    } else if (camera.stripeSubscription?.pause_collection) {
      // Fallback to old method if statusData is not available
      pauseDate = new Date(camera.stripeSubscription.current_period_end * 1000).toLocaleDateString();
      scheduledResumeDate = new Date(
        camera.stripeSubscription.pause_collection.resumes_at * 1000
      ).toLocaleDateString();
    }
  }

  // Option 1: Update the function to handle undefined values
  function formatDateToLocale(dateString: string | undefined): string {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  }

  const onPortalSession = async () => {
    try {
      const { data } = await createPortalSession({ customerId: camera.STRIPE_CUSTOMER_ID });
      window.open(data?.url, '_blank');
    } catch (error) {
      console.error(error);
    }
  };

  // Add state for confirmation modal
  const [confirmCancelPauseId, setConfirmCancelPauseId] = useState<string | null>(null);
  const [confirmResumeId, setConfirmResumeId] = useState<string | null>(null);
  const [resumeRequiresPaymentMethod, setResumeRequiresPaymentMethod] = useState(false);
  const [confirmTerminateId, setConfirmTerminateId] = useState<string | null>(null);

  // Refund options for the terminate modal (team-only). Preview is fetched
  // from Stripe when the modal opens; amounts are in pence.
  const [refundPreviewRequest, { isLoading: isLoadingRefundPreview }] = useRefundPreviewMutation();
  const [refundPreview, setRefundPreview] = useState<RefundPreviewData | null>(null);
  const [refundMode, setRefundMode] = useState<'none' | 'prorata' | 'custom'>('none');
  const [customRefund, setCustomRefund] = useState('');

  const closeTerminateModal = () => {
    setConfirmTerminateId(null);
    setRefundPreview(null);
    setRefundMode('none');
    setCustomRefund('');
  };

  // Fetch the refund preview from Stripe whenever the terminate modal opens.
  useEffect(() => {
    if (!confirmTerminateId) return;
    const staffSession = getStaffSessionToken();
    if (!staffSession) return;
    refundPreviewRequest({ customerId: confirmTerminateId })
      .unwrap()
      .then((res) => setRefundPreview(res))
      .catch((err) => {
        console.error(err);
        setRefundPreview(null);
        toast.error('Could not load refund details — only "no refund" is available');
      });
  }, [confirmTerminateId]);
  const [confirmTransferId, setConfirmTransferId] = useState<string | null>(null);
  const [transferCode, setTransferCode] = useState('');
  const [validatedTransferIccid, setValidatedTransferIccid] = useState<string | null>(null);
  const [isEuCustomer, setIsEuCustomer] = useState(false);
  const [currentTariffName, setCurrentTariffName] = useState<string | null>(null);
  const [networkChoice, setNetworkChoice] = useState<'uk_all' | 'uk_no_o2'>('uk_all');

  // Add-SIMs-to-plan modal state
  const [confirmAddSimsId, setConfirmAddSimsId] = useState<string | null>(null);
  const [addSimsCode, setAddSimsCode] = useState('');
  const [stagedSims, setStagedSims] = useState<{ code: string; iccid: string }[]>([]);
  const [addSimsNetworkChoice, setAddSimsNetworkChoice] = useState<'uk_all' | 'uk_no_o2'>('uk_all');
  const [addSimsNeedsNetworkChoice, setAddSimsNeedsNetworkChoice] = useState(false);
  // Derive the existing subscription's billing interval so the Add-SIMs flow
  // works correctly for both monthly and annual customers. Stripe will mirror
  // this interval when we create the new price.
  const subscriptionInterval = camera.stripeSubscription?.interval === 'year' ? 'year' : 'month';
  const intervalLabel = subscriptionInterval === 'year' ? 'year' : 'month';
  const intervalAdjective = subscriptionInterval === 'year' ? 'annual' : 'monthly';
  // Base price = what the customer is currently paying per SIM (Stripe's
  // plan.amount is in minor units / pence). Fall back to standard rack rate
  // if Stripe data is missing.
  const currentStripeAmountMinor = camera.stripeSubscription?.amount;
  const basePricePerSim =
    typeof currentStripeAmountMinor === 'number' && currentStripeAmountMinor > 0
      ? currentStripeAmountMinor / 100
      : subscriptionInterval === 'year'
        ? 720
        : 60;
  const existingSimQuantity = normaliseSimQuantity(camera.stripeSubscription?.quantity);
  // Start with the exact current Stripe unit price. Leaving this unchanged
  // preserves the existing discounted Price; a team edit applies to every SIM.
  const [unitPriceInput, setUnitPriceInput] = useState(basePricePerSim.toFixed(2));
  const [reviewBeforeSubmit, setReviewBeforeSubmit] = useState(false);
  const [addSimsResult, setAddSimsResult] = useState<
    | {
        message: string;
        previousQuantity: number;
        newQuantity: number;
        activatedCount: number;
        results: { iccid: string; status: string; message: string }[];
        stripeUpdateError: string | null;
        previousUnitAmount: number | null;
        newUnitAmount: number | null;
        currency: string | null;
      }
    | null
  >(null);

  // Create modal for cancel pause confirmation
  const { ModalComponent: CancelPauseModal } = useModal({
    isOpen: Boolean(confirmCancelPauseId),
    onClose: () => setConfirmCancelPauseId(null),
    title: 'Cancel Scheduled Pause'
  });

  // Create modal for resume-now confirmation (paused subscriptions)
  const { ModalComponent: ResumePlanModal } = useModal({
    isOpen: Boolean(confirmResumeId),
    onClose: () => {
      if (isResumingPlan) return;
      setConfirmResumeId(null);
      setResumeRequiresPaymentMethod(false);
    },
    title: 'Resume Plan Now'
  });

  // Create modal for terminate SIM confirmation
  const { ModalComponent: TerminateSimModal } = useModal({
    isOpen: Boolean(confirmTerminateId),
    onClose: closeTerminateModal,
    title: 'Terminate SIM'
  });

  const { ModalComponent: SimTransferModal } = useModal({
    isOpen: Boolean(confirmTransferId),
    onClose: () => {
      setConfirmTransferId(null);
      setTransferCode('');
      setValidatedTransferIccid(null);
      setIsEuCustomer(false);
      setCurrentTariffName(null);
      setNetworkChoice('uk_all');
    },
    title: 'SIM Transfer'
  });

  const { ModalComponent: AddSimsModal } = useModal({
    isOpen: Boolean(confirmAddSimsId),
    onClose: () => {
      setConfirmAddSimsId(null);
      setAddSimsCode('');
      setStagedSims([]);
      setUnitPriceInput(basePricePerSim.toFixed(2));
      setReviewBeforeSubmit(false);
      setAddSimsResult(null);
      setAddSimsNeedsNetworkChoice(false);
    },
    title: 'Add SIMs to your plan'
  });

  // Function to handle the cancel pause confirmation
  const handleCancelPause = () => {
    if (!confirmCancelPauseId) return;

    // Close modal immediately to avoid double-clicks
    const customerId = confirmCancelPauseId;
    setConfirmCancelPauseId(null);

    // First make the API call and wait for it to complete
    cancelPauseSubscription({ customerId })
      .unwrap()
      .then(() => {
        toast.success('Pause canceled successfully');

        // Add a small delay to ensure backend has processed the change
        setTimeout(() => {
          // Then trigger refresh to get updated data
          if (onRefreshData) {
            onRefreshData();
          }
        }, 1000); // 1 second delay to ensure server has processed the cancellation
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to cancel pause');
      });
  };

  // Function to handle resuming a paused subscription immediately
  const handleResumeNow = () => {
    if (!confirmResumeId || isResumingPlan) return;
    const customerId = confirmResumeId;
    setResumeRequiresPaymentMethod(false);
    deactivatePause({ customerId })
      .unwrap()
      .then(() => {
        setConfirmResumeId(null);
        toast.success('Your plan has been resumed');
        setTimeout(() => { if (onRefreshData) onRefreshData(); }, 1000);
      })
      .catch((err) => {
        console.error(err);
        if (err?.data?.code === 'payment_method_required') {
          setResumeRequiresPaymentMethod(true);
          return;
        }
        toast.error(
          err?.data?.message || 'Failed to resume your plan. Please try again or contact support.'
        );
      });
  };

  const openResumePaymentDetails = async () => {
    if (!confirmResumeId) return;
    try {
      const portal = await createPortalSession({ customerId: confirmResumeId }).unwrap();
      if (!portal.url) throw new Error('Missing billing portal URL');
      window.location.assign(portal.url);
    } catch (error) {
      console.error(error);
      toast.error('Could not open payment details. Please contact support.');
    }
  };

  // Function to handle terminate SIM
  const handleTerminateSim = () => {
    if (!confirmTerminateId) return;
    const staffSession = getStaffSessionToken();
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      closeTerminateModal();
      return;
    }

    // Validate the refund choice before closing the modal
    let refundAmountPence: number | undefined;
    if (refundMode === 'custom') {
      const parsed = Math.round(parseFloat(customRefund) * 100);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        toast.error('Enter a custom refund amount greater than zero');
        return;
      }
      if (refundPreview && parsed > refundPreview.max_refund_pence) {
        toast.error(
          `Refund cannot exceed the last payment (max ${formatMoney(refundPreview.max_refund_pence, refundPreview.currency)})`
        );
        return;
      }
      refundAmountPence = parsed;
    }

    const customerId = confirmTerminateId;
    const chosenRefundMode = refundMode;
    closeTerminateModal();
    terminateSim({
      customerId,
      simIccid: camera.SIM_ICCID,

      refundMode: chosenRefundMode,
      refundAmountPence
    })
      .unwrap()
      .then((res) => {
        const refund = res.refund;
        if (refund?.status === 'succeeded') {
          toast.success(
            `SIM ${camera.SIM_ICCID} terminated — ${formatMoney(refund.amount_pence, refund.currency)} refunded`
          );
        } else if (refund?.status === 'failed') {
          toast.warn(
            `SIM ${camera.SIM_ICCID} terminated, but the refund FAILED — please issue it manually in Stripe`
          );
        } else {
          toast.success(`SIM ${camera.SIM_ICCID} terminated successfully`);
        }
        setTimeout(() => { if (onRefreshData) onRefreshData(); }, 1000);
      })
      .catch((err) => {
        console.error(err);
        const message = err?.data?.message;
        toast.error(message ? `Failed to terminate SIM: ${message}` : 'Failed to terminate SIM. Please try again.');
      });
  };

  const handleInitiateTransfer = () => {
    if (!confirmTransferId) return;
    const staffSession = getStaffSessionToken();
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      return;
    }

    const trimmedCode = transferCode.trim().toUpperCase();
    if (!trimmedCode) {
      toast('Please enter the 4-character activation code', { type: 'error' });
      return;
    }

    initiateSimTransfer({
      customerId: confirmTransferId,
      currentSimIccid: camera.SIM_ICCID,
      transferCode: trimmedCode,

    })
      .unwrap()
      .then((res) => {
        setValidatedTransferIccid(res.target_sim_iccid);
        setIsEuCustomer(Boolean(res.is_eu_customer));
        setCurrentTariffName(res.current_tariff_name ?? null);
        // Reset to default UK All for non-EU customers; ignored for EU.
        setNetworkChoice('uk_all');
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to validate transfer code. Please check the code and try again.');
      });
  };

  const handleConfirmTransferIccid = () => {
    if (!validatedTransferIccid || !confirmTransferId) return;
    const staffSession = getStaffSessionToken();
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      return;
    }

    confirmSimTransfer({
      customerId: confirmTransferId,
      currentSimIccid: camera.SIM_ICCID,
      transferCode: transferCode.trim().toUpperCase(),
      targetSimIccid: validatedTransferIccid,
      networkChoice: isEuCustomer ? undefined : networkChoice,

    })
      .unwrap()
      .then((res) => {
        toast.success(
          `SIM transfer complete: ${res.old_sim_iccid} → ${res.new_sim_iccid} on ${res.tariff_name}`
        );
        setConfirmTransferId(null);
        setTransferCode('');
        setValidatedTransferIccid(null);
        setIsEuCustomer(false);
        setCurrentTariffName(null);
        setNetworkChoice('uk_all');
        setTimeout(() => {
          if (onRefreshData) onRefreshData();
        }, 1000);
      })
      .catch((err) => {
        console.error(err);
        const message = err?.data?.message || 'Failed to complete SIM transfer.';
        toast.error(message);
      });
  };

  const handleResolveActivationCode = () => {
    if (!confirmAddSimsId) return;
    const staffSession = getStaffSessionToken();
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      return;
    }
    const code = addSimsCode.trim().toUpperCase();
    if (!/^[A-Z]{4}$/.test(code)) {
      toast('Activation code must be exactly 4 letters', { type: 'error' });
      return;
    }
    if (stagedSims.some((s) => s.code === code)) {
      toast('This activation code is already staged', { type: 'error' });
      return;
    }

    resolveActivationCode({
      activationCode: code,
      existingSimIccid: camera.SIM_ICCID,
      stagedIccids: stagedSims.map((s) => s.iccid),

    })
      .unwrap()
      .then((res) => {
        setStagedSims((prev) => [...prev, { code: res.activation_code, iccid: res.iccid }]);
        setAddSimsCode('');
      })
      .catch((err) => {
        console.error(err);
        const message = err?.data?.message || 'Failed to resolve activation code.';
        toast.error(message);
      });
  };

  const handleRemoveStaged = (iccid: string) => {
    setStagedSims((prev) => prev.filter((s) => s.iccid !== iccid));
  };

  const handleAddSimsToPlan = () => {
    if (!confirmAddSimsId) return;
    const staffSession = getStaffSessionToken();
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      return;
    }
    if (stagedSims.length === 0) {
      toast('Stage at least one SIM before activating', { type: 'error' });
      return;
    }

    const quote = getAddSimsQuote(
      existingSimQuantity,
      stagedSims.length,
      unitPriceInput,
      basePricePerSim
    );
    if (!quote) {
      toast('Unit price must be a valid amount of zero or more', { type: 'error' });
      return;
    }

    // Null means Stripe keeps the exact current price id. Only create or reuse
    // a bespoke Stripe Price when the team has deliberately changed the value.
    const customUnitAmount = quote.changesUnitPrice ? quote.unitPrice : null;

    addSimsToPlan({
      customerId: confirmAddSimsId,
      existingSimIccid: camera.SIM_ICCID,
      newIccids: stagedSims.map((s) => s.iccid),

      customUnitAmount,
      networkChoice: addSimsNeedsNetworkChoice ? addSimsNetworkChoice : null
    })
      .unwrap()
      .then((res) => {
        setAddSimsResult({
          message: res.message,
          previousQuantity: res.previous_quantity,
          newQuantity: res.new_quantity,
          activatedCount: res.activated_count,
          results: res.results,
          stripeUpdateError: res.stripe_update_error,
          previousUnitAmount: res.previous_unit_amount,
          newUnitAmount: res.new_unit_amount,
          currency: res.currency
        });
        if (res.success) {
          toast.success(
            `Added ${res.activated_count} SIM(s). Plan quantity ${res.previous_quantity} → ${res.new_quantity}.`
          );
        } else {
          toast.warning(res.message);
        }
        setTimeout(() => {
          if (onRefreshData) onRefreshData();
        }, 1000);
      })
      .catch((err) => {
        console.error(err);
        // If backend says we need a network choice, show the selector
        if (err?.data?.requires_network_choice) {
          setAddSimsNeedsNetworkChoice(true);
          setReviewBeforeSubmit(false);
          toast.info('Please select a network plan for the new SIMs');
          return;
        }
        const message = err?.data?.message || 'Failed to add SIMs to plan.';
        const results = err?.data?.results as
          | { iccid: string; status: string; message: string }[]
          | undefined;
        if (results && results.length > 0) {
          setAddSimsResult({
            message,
            previousQuantity: 0,
            newQuantity: 0,
            activatedCount: 0,
            results,
            stripeUpdateError: null,
            previousUnitAmount: null,
            newUnitAmount: null,
            currency: null
          });
        }
        toast.error(message);
      });
  };

  return (
    <>
    <Container>
      <MainContent>
        <TitleSection>
          <Title>{camera.nicknameInDatabase}</Title>
          <EditBtn
            type="button"
            onClick={() => onEditCamera(camera)}
            title="Rename this camera"
            aria-label="Rename this camera">
            <PencilIcon size={14} />
            {hasDefaultNickname && <EditBtnLabel>Rename</EditBtnLabel>}
          </EditBtn>
        </TitleSection>

        <SimSection>
          <SectionLabel>SIM Number</SectionLabel>
          <SimInfoRow>
            <SimNumber>{camera.SIM_ICCID}</SimNumber>
            <Status labelDashboard={labelDashboard}>{labelDashboard}</Status>
          </SimInfoRow>
        </SimSection>

        {!isTerminatedSim && (isActive || isOverdue || (pendingCancellation && !isPaymentFailed) || isPauseScheduled) && (
          <CombinedPlanContainer>
            <PlanHeader>
              <PlanBadge>{getSubscriptionPlanDisplay()}</PlanBadge>
              <BillingInfo>
                {camera.stripeSubscription?.interval === 'month' ? 'Billed Monthly' : 'Billed Annually'}
                {camera.stripeSubscription?.group_order_id && (
                  <span style={{ display: 'block', fontSize: '0.75rem', marginTop: 4 }}>
                    Shared plan · {camera.stripeSubscription.quantity || 1} SIMs
                  </span>
                )}
              </BillingInfo>
            </PlanHeader>
            <DataSection>
              <DataLabel>Data Usage</DataLabel>
              <DataValue>{camera.dataUsageGB} GB</DataValue>
            </DataSection>
          </CombinedPlanContainer>
        )}

        {(!isActive && !isOverdue && !(pendingCancellation && !isPaymentFailed) && !isPauseScheduled && !isPaymentFailed && !isTerminatedSim) && (
          <PlanOnlySection>
            <SectionLabel>Subscription Plan</SectionLabel>
            <PlanText>{getSubscriptionPlanDisplay()}</PlanText>
          </PlanOnlySection>
        )}

        {isTerminatedSim && (
          <TerminatedContainer>
            <PlanHeader>
              <TerminatedBadge>Terminated</TerminatedBadge>
              <BillingInfo>No active subscription</BillingInfo>
            </PlanHeader>
            <TerminatedBody>
              Your SIM has been terminated either due to lack of payment, lack of instruction, or we haven't heard back from you.
            </TerminatedBody>
          </TerminatedContainer>
        )}
      </MainContent>
      <BottomWrapper>
        <ActionsWrapper>
          <ActionContainer to={`/invoices/${camera.STRIPE_CUSTOMER_ID}`}>
            <PaperIcon />
            <p>Invoices</p>
          </ActionContainer>
          {isActive && (
            <ActionContainer to={`/subscription/${camera.STRIPE_CUSTOMER_ID}`}>
              <ChangeIcon />
              <p>Manage plan</p>
            </ActionContainer>
          )}
          {isActive && isHeavyStreamingPlan() && (
            <ActionContainer to="/livestreaming/setup">
              <LivestreamIcon />
              <p>Setup Livestreaming to Website</p>
            </ActionContainer>
          )}
          {isPaused && (
            <>
              <ActionContainer
                to={'#'}
                onClick={() => setConfirmResumeId(camera.STRIPE_CUSTOMER_ID)}>
                <ChangeIcon />
                <p>Resume now</p>
              </ActionContainer>
              <StyledText>
                Your subscription is currently paused and will resume on {resumeDate}. Need it back
                sooner? Use “Resume now” to restart your plan straight away.
              </StyledText>
            </>
          )}
          {isPauseScheduled && (
            <>
              <ActionContainer to={'#'} onClick={() => onOpenPauseModal(camera.STRIPE_CUSTOMER_ID)}>
                <ChangeIcon />
                <p>Alter pause date</p>
              </ActionContainer>
              <ActionContainer
                to={'#'}
                onClick={() => setConfirmCancelPauseId(camera.STRIPE_CUSTOMER_ID)}>
                <ChangeIcon />
                <p>Cancel pause</p>
              </ActionContainer>
              <StyledText>
                Your subscription is scheduled to pause on {pauseDate} and will resume on {scheduledResumeDate}.
              </StyledText>
            </>
          )}
          {isOverdue && (
            <>
              <ActionContainer to={`#`} onClick={onPortalSession}>
                <ChangeIcon />
                <p>Pay now</p>
              </ActionContainer>
              <StyledText>
                Overdue payments on account. Please pay now or your Streaming Plan will be
                terminated.
              </StyledText>
            </>
          )}
          {pendingCancellation && (
            <>
              <ActionContainer to={`/pricing/${camera.SIM_ICCID}/${camera.STRIPE_CUSTOMER_ID}`}>
                <ChangeIcon />
                <p>Renew subscription</p>
              </ActionContainer>
              <StyledText>
                No active subscription found, please renew immediately or your
                Streaming Plan will be terminated.
              </StyledText>
            </>
          )}
          {isTerminatedSim && (
            <OrderReplacementButton
              href={replacementSimUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <p>📦 Order a free replacement SIM →</p>
            </OrderReplacementButton>
          )}
          {!isTerminatedSim && !isSolarPlan() && snapshotUrl && (
            <SnapshotBlock>
              <SnapshotFrame>
                <SnapshotImage
                  key={snapshotKey}
                  src={snapshotUrl}
                  alt={`Snapshot for ${camera.SIM_ICCID}`}
                />
                <SnapshotRefreshButton
                  type="button"
                  onClick={handleFetchSnapshot}
                  disabled={snapshotLoading || customerRefreshLoading}
                  title={(snapshotLoading || customerRefreshLoading) ? 'Capturing new snapshot…' : 'Capture new snapshot'}
                  aria-label="Refresh snapshot">
                  <span className={(snapshotLoading || customerRefreshLoading) ? 'spin' : ''}>⟳</span>
                </SnapshotRefreshButton>
                {snapshotTakenAt && (
                  <SnapshotTimestamp>
                    {new Date(snapshotTakenAt).toLocaleString()}
                  </SnapshotTimestamp>
                )}
              </SnapshotFrame>
              {snapshotError && (
                <StyledText style={{ color: '#c0392b' }}>{snapshotError}</StyledText>
              )}
            </SnapshotBlock>
          )}
          {isAdmin && !isTerminatedSim && !camera.recentTerminationRequest && (
            <TransferButton
              type="button"
              onClick={() => setConfirmTransferId(camera.STRIPE_CUSTOMER_ID)}>
              <p>↔ SIM Transfer</p>
            </TransferButton>
          )}
          {isAdmin && !isTerminatedSim && !camera.recentTerminationRequest && (
            <TransferButton
              type="button"
              onClick={() => {
                setUnitPriceInput(basePricePerSim.toFixed(2));
                setConfirmAddSimsId(camera.STRIPE_CUSTOMER_ID);
              }}>
              <p>+ Add SIMs to plan</p>
            </TransferButton>
          )}
          {isAdmin && !isTerminatedSim && !camera.recentTerminationRequest && (
            <TerminateButton
              type="button"
              onClick={() => setConfirmTerminateId(camera.STRIPE_CUSTOMER_ID)}>
              <p>⚠ Terminate SIM</p>
            </TerminateButton>
          )}
          {isAdmin && !isTerminatedSim && camera.recentTerminationRequest && (
            <StyledText>
              A termination request has already been made for this SIM in the last 30 days.
            </StyledText>
          )}
        </ActionsWrapper>
      </BottomWrapper>
      {!isCanceled && !pendingCancellation && !isPaused && (
        <StyledLink to={`/camera/${camera.SIM_ICCID}`}>
          Data Usage
          <ArrowRightIcon color={colors.primary} size={20} />
        </StyledLink>
      )}
    </Container>
      <CancelPauseModal>
        <ModalContent>
          <p>Are you sure you want to cancel the scheduled pause?</p>
          <ButtonWrapper>
            <Button onClick={handleCancelPause} isLoading={isCancelingPause}>
              Yes
            </Button>
            <Button variant="secondary" onClick={() => setConfirmCancelPauseId(null)}>
              Cancel
            </Button>
          </ButtonWrapper>
        </ModalContent>
      </CancelPauseModal>
      <ResumePlanModal>
        <ModalContent>
          {resumeRequiresPaymentMethod ? (
            <>
              <p role="alert">Choose a default payment method in your billing settings before restarting this plan. Your scheduled restart is unchanged.</p>
              <ButtonWrapper>
                <Button onClick={openResumePaymentDetails}>Update payment details</Button>
                <Button variant="secondary" onClick={() => {
                  setConfirmResumeId(null);
                  setResumeRequiresPaymentMethod(false);
                }}>Close</Button>
              </ButtonWrapper>
            </>
          ) : (
            <>
              {isResumingPlan ? (
                <ResumeProgress role="status" aria-live="polite">
                  <ResumeProgressWheel aria-hidden="true" />
                  <span>Restarting your plan… This may take a moment.</span>
                </ResumeProgress>
              ) : (
                <>
                  <p>Resume your plan now?</p>
                  <p style={{ fontSize: '0.85rem' }}>
                    Billing restarts straight away and your camera will be back online shortly. The
                    scheduled resume date ({resumeDate}) will no longer apply.
                  </p>
                </>
              )}
              <ButtonWrapper>
                <Button onClick={handleResumeNow} isLoading={isResumingPlan} loadingText="Resuming…">
                  Yes, resume now
                </Button>
                <Button variant="secondary" disabled={isResumingPlan} onClick={() => setConfirmResumeId(null)}>
                  Cancel
                </Button>
              </ButtonWrapper>
            </>
          )}
        </ModalContent>
      </ResumePlanModal>
      <TerminateSimModal>
        <ModalContent>
          <p style={{ fontWeight: 'bold', color: '#c0392b' }}>⚠ This will immediately cancel the Stripe subscription and cannot be undone.</p>
          <p>SIM: <strong>{camera.SIM_ICCID}</strong></p>
          <p>A termination notification email will be sent.</p>

          <div style={{ margin: '8px 0', borderTop: '1px solid #ddd', paddingTop: 8 }}>
            <p style={{ fontWeight: 'bold', marginBottom: 6 }}>Refund</p>
            {isLoadingRefundPreview ? (
              <p style={{ fontSize: '0.85rem' }}>Checking last payment…</p>
            ) : refundPreview?.refund_available ? (
              <>
                <p style={{ fontSize: '0.85rem' }}>
                  Last payment:{' '}
                  <strong>{formatMoney(refundPreview.last_payment_pence, refundPreview.currency)}</strong>
                  {refundPreview.payment_date
                    ? ` on ${new Date(refundPreview.payment_date * 1000).toLocaleDateString('en-GB')}`
                    : null}
                  {refundPreview.already_refunded_pence > 0 && (
                    <> ({formatMoney(refundPreview.already_refunded_pence, refundPreview.currency)} already refunded)</>
                  )}
                </p>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <input
                    type="radio"
                    name="refundMode"
                    value="none"
                    checked={refundMode === 'none'}
                    onChange={() => setRefundMode('none')}
                  />{' '}
                  No refund
                </label>
                {refundPreview.prorata_pence !== null && (
                  <label style={{ display: 'block', marginBottom: 4 }}>
                    <input
                      type="radio"
                      name="refundMode"
                      value="prorata"
                      checked={refundMode === 'prorata'}
                      onChange={() => setRefundMode('prorata')}
                    />{' '}
                    Pro-rata: <strong>{formatMoney(refundPreview.prorata_pence, refundPreview.currency)}</strong>
                    {refundPreview.days_remaining !== null && refundPreview.days_total !== null && (
                      <span style={{ fontSize: '0.85rem' }}>
                        {' '}({refundPreview.days_remaining} of {refundPreview.days_total} days unused)
                      </span>
                    )}
                  </label>
                )}
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <input
                    type="radio"
                    name="refundMode"
                    value="custom"
                    checked={refundMode === 'custom'}
                    onChange={() => setRefundMode('custom')}
                  />{' '}
                  Custom amount
                </label>
                {refundMode === 'custom' && (
                  <div style={{ marginLeft: 22 }}>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      max={(refundPreview.max_refund_pence / 100).toFixed(2)}
                      value={customRefund}
                      onChange={(e) => setCustomRefund(e.target.value)}
                      placeholder="0.00"
                      style={{ width: 120, padding: '4px 6px' }}
                    />
                    <p style={{ fontSize: '0.8rem', margin: '4px 0 0' }}>
                      Max {formatMoney(refundPreview.max_refund_pence, refundPreview.currency)} (last payment
                      {refundPreview.already_refunded_pence > 0 ? ' minus previous refunds' : ''})
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p style={{ fontSize: '0.85rem' }}>
                No refundable payment found — the plan will be cancelled without a refund.
              </p>
            )}
          </div>

          <ButtonWrapper>
            <Button variant="danger" onClick={handleTerminateSim} isLoading={isTerminating}>
              {refundMode === 'none' || !refundPreview?.refund_available
                ? 'Yes, Terminate'
                : 'Yes, Terminate & Refund'}
            </Button>
            <Button variant="secondary" onClick={closeTerminateModal}>
              Cancel
            </Button>
          </ButtonWrapper>
        </ModalContent>
      </TerminateSimModal>
      <SimTransferModal>
        <ModalContent>
          {validatedTransferIccid ? (
            <>
              <p style={{ fontWeight: 'bold' }}>Please confirm this ICCID is correct</p>
              <p>Current SIM: <strong>{camera.SIM_ICCID}</strong></p>
              <p>Activation code: <strong>{transferCode.trim().toUpperCase()}</strong></p>
              <p>Resolved ICCID: <strong>{validatedTransferIccid}</strong></p>

              {isEuCustomer ? (
                <p style={{
                  fontSize: '0.9rem',
                  background: '#eef6ff',
                  border: '1px solid #b6d4fe',
                  padding: '8px 10px',
                  borderRadius: 4
                }}>
                  Customer is on an EU plan
                  {currentTariffName ? <> (<em>{currentTariffName}</em>)</> : null}
                  {' '}— new SIM will be provisioned on{' '}
                  <strong>Pelion EU 2 Networks (£60/mo)</strong>.
                </p>
              ) : (
                <div style={{ margin: '8px 0' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: 6 }}>Network plan for new SIM</p>
                  <label style={{ display: 'block', marginBottom: 4 }}>
                    <input
                      type="radio"
                      name="networkChoice"
                      value="uk_all"
                      checked={networkChoice === 'uk_all'}
                      onChange={() => setNetworkChoice('uk_all')}
                    />{' '}
                    UK — all 4 networks
                  </label>
                  <label style={{ display: 'block' }}>
                    <input
                      type="radio"
                      name="networkChoice"
                      value="uk_no_o2"
                      checked={networkChoice === 'uk_no_o2'}
                      onChange={() => setNetworkChoice('uk_no_o2')}
                    />{' '}
                    UK — excluding O2
                  </label>
                </div>
              )}

              <p style={{ fontSize: '0.85rem', color: '#c0392b' }}>
                ⚠ Confirming will activate the new SIM in Pelion on the selected plan,
                terminate the old SIM in our database, and terminate the old SIM in Pelion via API.
                The Stripe subscription stays unchanged.
              </p>
              <ButtonWrapper>
                <Button onClick={handleConfirmTransferIccid} isLoading={isConfirmingTransfer}>
                  Yes, ICCID is correct
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setValidatedTransferIccid(null);
                  }}>
                  Back
                </Button>
              </ButtonWrapper>
            </>
          ) : (
            <>
              <p style={{ fontWeight: 'bold' }}>Enter transfer activation code</p>
              <p>Current SIM: <strong>{camera.SIM_ICCID}</strong></p>
              <TransferCodeInput
                maxLength={4}
                value={transferCode}
                onChange={(e) => setTransferCode(e.target.value.toUpperCase())}
                placeholder="ABCD"
              />
              <p style={{ fontSize: '0.85rem' }}>
                Confirming will validate the code and show the ICCID for confirmation.
              </p>
              <ButtonWrapper>
                <Button onClick={handleInitiateTransfer} isLoading={isInitiatingTransfer}>
                  Validate Code
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setConfirmTransferId(null);
                    setTransferCode('');
                    setValidatedTransferIccid(null);
                  }}>
                  Cancel
                </Button>
              </ButtonWrapper>
            </>
          )}
        </ModalContent>
      </SimTransferModal>
      <AddSimsModal>
        <ModalContent>
          {addSimsResult ? (
            <>
              <p style={{ fontWeight: 'bold' }}>Add SIMs result</p>
              <p>{addSimsResult.message}</p>
              {addSimsResult.activatedCount > 0 && (
                <p>
                  Stripe quantity:{' '}
                  <strong>
                    {addSimsResult.previousQuantity} → {addSimsResult.newQuantity}
                  </strong>
                </p>
              )}
              {addSimsResult.stripeUpdateError && (
                <p style={{ color: '#c0392b', fontSize: '0.85rem' }}>
                  ⚠ Stripe quantity update failed: {addSimsResult.stripeUpdateError}
                </p>
              )}
              {addSimsResult.results.length > 0 && (
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    padding: 8,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace'
                  }}>
                  {addSimsResult.results.map((r) => (
                    <div
                      key={r.iccid}
                      style={{
                        color: r.status === 'activated' ? '#1e7e34' : '#c0392b',
                        marginBottom: 4
                      }}>
                      {r.iccid} — <strong>{r.status}</strong>: {r.message}
                    </div>
                  ))}
                </div>
              )}
              <ButtonWrapper>
                <Button
                  onClick={() => {
                    setConfirmAddSimsId(null);
                    setAddSimsCode('');
                    setStagedSims([]);
                    setUnitPriceInput(basePricePerSim.toFixed(2));
                    setAddSimsResult(null);
                  }}>
                  Close
                </Button>
              </ButtonWrapper>
            </>
          ) : reviewBeforeSubmit ? (
            (() => {
              const quote = getAddSimsQuote(
                existingSimQuantity,
                stagedSims.length,
                unitPriceInput,
                basePricePerSim
              );
              if (!quote) return null;
              return (
                <>
                  <p style={{ fontWeight: 'bold' }}>Confirm your order</p>
                  <div
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      padding: 12,
                      fontSize: '0.9rem',
                      background: '#fafafa'
                    }}>
                    <div>
                      Existing SIM: <strong>{camera.SIM_ICCID}</strong>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      Adding {stagedSims.length} new SIM{stagedSims.length === 1 ? '' : 's'}:
                      <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {stagedSims.map((s) => (
                          <li key={s.iccid}>
                            {s.code} → {s.iccid}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '10px 0' }} />
                    <div>
                      Plan size:{' '}
                      <strong>
                        {quote.existingQuantity} → {quote.newQuantity} SIMs
                      </strong>
                    </div>
                    <div>
                      Price per SIM:{' '}
                      <strong>£{quote.unitPrice.toFixed(2)}</strong> / {intervalLabel}
                    </div>
                    <div style={{ color: quote.changesUnitPrice ? '#b45309' : '#2e7d32' }}>
                      {quote.changesUnitPrice
                        ? `Price changed from £${basePricePerSim.toFixed(2)} for every SIM on this subscription.`
                        : 'The existing discounted price will be retained.'}
                    </div>
                    <div style={{ marginTop: 6, fontSize: '1rem' }}>
                      New {intervalAdjective} total:{' '}
                      <strong>£{quote.total.toFixed(2)}</strong>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 8 }}>
                    The new SIMs will be activated immediately and the customer will be charged the
                    prorated difference.
                  </p>
                  <ButtonWrapper>
                    <Button
                      onClick={() => {
                        handleAddSimsToPlan();
                      }}
                      isLoading={isAddingSims}>
                      Confirm &amp; pay
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => setReviewBeforeSubmit(false)}
                      disabled={isAddingSims}>
                      Back
                    </Button>
                  </ButtonWrapper>
                </>
              );
            })()
          ) : (
            (() => {
              const quote = getAddSimsQuote(
                existingSimQuantity,
                stagedSims.length,
                unitPriceInput,
                basePricePerSim
              );
              const canContinue = stagedSims.length > 0 && quote !== null;

              return (
                <>
                  <p style={{ fontSize: '0.9rem', margin: 0 }}>
                    Add more SIMs to the same plan as <strong>{camera.SIM_ICCID}</strong>. Enter
                    the 4-letter activation code printed on each new SIM — it'll appear in your
                    list once validated.
                  </p>

                  <div style={{ marginTop: 12 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      1. Add a SIM
                    </label>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleResolveActivationCode();
                      }}
                      style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                      <TransferCodeInput
                        maxLength={4}
                        value={addSimsCode}
                        onChange={(e) => setAddSimsCode(e.target.value.toUpperCase())}
                        placeholder="ABCD"
                        autoFocus
                      />
                      <Button
                        type="submit"
                        onClick={handleResolveActivationCode}
                        isLoading={isResolvingCode}
                        disabled={addSimsCode.trim().length !== 4}>
                        Add SIM
                      </Button>
                    </form>
                  </div>

                  {stagedSims.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                        2. SIMs to add ({stagedSims.length})
                      </label>
                      <div
                        style={{
                          maxHeight: 180,
                          overflowY: 'auto',
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          padding: 8,
                          fontSize: '0.8rem'
                        }}>
                        {stagedSims.map((s) => (
                          <div
                            key={s.iccid}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 8,
                              padding: '4px 0',
                              borderBottom: '1px solid #f0f0f0'
                            }}>
                            <span style={{ fontFamily: 'monospace' }}>
                              <strong>{s.code}</strong>
                              <span style={{ color: '#666' }}> → {s.iccid}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveStaged(s.iccid)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#c0392b',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                padding: '0 4px'
                              }}
                              title="Remove">
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {stagedSims.length > 0 && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        background: '#f5faff',
                        border: '1px solid #cfe2ff',
                        borderRadius: 6,
                        fontSize: '0.85rem'
                      }}>
                      <div>
                        Plan size:{' '}
                        <strong>
                          {existingSimQuantity} → {existingSimQuantity + stagedSims.length} SIMs
                        </strong>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        Current price: £{basePricePerSim.toFixed(2)} / SIM / {intervalLabel}
                      </div>
                      {quote && (
                        <>
                          <div
                            style={{
                              marginTop: 4,
                              color: quote.changesUnitPrice ? '#b45309' : '#2e7d32'
                            }}>
                            {quote.changesUnitPrice
                              ? `New price applies to all ${quote.newQuantity} SIMs.`
                              : 'Existing discounted price will carry forward.'}
                          </div>
                          <div style={{ marginTop: 6, fontSize: '0.95rem' }}>
                            New {intervalAdjective} total:{' '}
                            <strong>£{quote.total.toFixed(2)}</strong>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 12 }}>
                    <label
                      htmlFor={`add-sims-price-${camera.SIM_ICCID}`}
                      style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: 6 }}>
                      Price per SIM (£/{intervalLabel})
                    </label>
                    <input
                      id={`add-sims-price-${camera.SIM_ICCID}`}
                      type="number"
                      min={0}
                      step="0.01"
                      value={unitPriceInput}
                      onChange={(e) => setUnitPriceInput(e.target.value)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #00000020',
                        borderRadius: '0.5rem',
                        width: 140
                      }}
                    />
                    <p style={{ fontSize: '0.75rem', color: '#666', margin: '4px 0 0 0' }}>
                      This starts at the customer's current Stripe price. Change it only when an
                      agreed price should apply to the whole subscription.
                    </p>
                    {!quote && (
                      <p style={{ fontSize: '0.75rem', color: '#c0392b', margin: '4px 0 0 0' }}>
                        Enter a valid price of zero or more.
                      </p>
                    )}
                  </div>

                  {addSimsNeedsNetworkChoice && (
                    <div
                      style={{
                        marginTop: 12,
                        padding: 12,
                        background: '#eef6ff',
                        border: '1px solid #b6d4fe',
                        borderRadius: 6,
                        fontSize: '0.85rem'
                      }}>
                      <p style={{ fontWeight: 'bold', marginBottom: 8, margin: 0 }}>
                        Select network plan for new SIMs
                      </p>
                      <p style={{ fontSize: '0.8rem', color: '#666', marginTop: 4, marginBottom: 8 }}>
                        The existing SIM is on an older plan. Please choose which network to provision the new SIMs on:
                      </p>
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <input
                          type="radio"
                          name="addSimsNetworkChoice"
                          value="uk_all"
                          checked={addSimsNetworkChoice === 'uk_all'}
                          onChange={() => setAddSimsNetworkChoice('uk_all')}
                        />{' '}
                        UK — all 4 networks
                      </label>
                      <label style={{ display: 'block' }}>
                        <input
                          type="radio"
                          name="addSimsNetworkChoice"
                          value="uk_no_o2"
                          checked={addSimsNetworkChoice === 'uk_no_o2'}
                          onChange={() => setAddSimsNetworkChoice('uk_no_o2')}
                        />{' '}
                        UK — excluding O2
                      </label>
                    </div>
                  )}

                  <ButtonWrapper>
                    <Button
                      onClick={() => {
                        if (!quote) {
                          toast('Unit price must be a valid amount of zero or more', { type: 'error' });
                          return;
                        }
                        setReviewBeforeSubmit(true);
                      }}
                      disabled={!canContinue}>
                      {stagedSims.length > 0
                        ? `Continue — add ${stagedSims.length} SIM${stagedSims.length === 1 ? '' : 's'}`
                        : 'Continue'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setConfirmAddSimsId(null);
                        setAddSimsCode('');
                        setStagedSims([]);
                        setUnitPriceInput(basePricePerSim.toFixed(2));
                        setAddSimsNeedsNetworkChoice(false);
                      }}>
                      Cancel
                    </Button>
                  </ButtonWrapper>
                </>
              );
            })()
          )}
        </ModalContent>
      </AddSimsModal>
    </>
  );
};

const StyledText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  color: ${({ theme }) => theme.colors.red};
`;

const EditBtn = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.primary}55;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  transition: all 0.2s ease;
  flex-shrink: 0;
  line-height: 1;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }

  &:hover svg path {
    stroke: ${({ theme }) => theme.colors.white};
    fill: ${({ theme }) => theme.colors.white};
  }
`;

const EditBtnLabel = styled.span`
  line-height: 1;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-grow: 1;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray500}30;
`;

const SimSection = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gray}50, ${({ theme }) => theme.colors.white});
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const SectionLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.extraSmall};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
  text-transform: uppercase;
  letter-spacing: 0.025em;
  opacity: 0.8;
  display: block;
  margin-bottom: 0.5rem;
`;

const SimInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SimNumber = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
`;

const PlanOnlySection = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}08, ${({ theme }) => theme.colors.primary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}25;
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
`;

const Status = styled.div<{ labelDashboard: string }>`
  display: flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  background-color: ${({ theme, labelDashboard }) => {
    switch (labelDashboard) {
      case 'active':
        return theme.colors.green900;
      case 'Attention':
        return theme.colors.red;
      case 'Barred':
        return theme.colors.red;
      case 'canceled':
        return theme.colors.red;
      case 'payment failed':
        return theme.colors.red;
      case 'paused':
        return theme.colors.orange;
      case 'pause scheduled':  // Add this new case
        return theme.colors.orange;
      case 'overdue':
        return theme.colors.red;
      case 'past_due':
        return theme.colors.red;

      case 'terminated':
        return theme.colors.gray900;
      default:
        return theme.colors.gray500;
    }
  }};
  font-size: 0.65rem;
  margin-top: 0.2rem;
  border-radius: 0.8rem;
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.white};
`;

const BottomWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  height: 100%;
`;

const StyledLink = styled(Link)`
  align-self: flex-end;
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}10;
    transform: translateX(3px);
  }
`;

const ActionContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary200}, ${({ theme }) => theme.colors.primary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}20;
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  width: 100%;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}20, ${({ theme }) => theme.colors.primary}25);
    border-color: ${({ theme }) => theme.colors.primary}40;
  }
`;

const TerminateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff5f5;
  border: 1px solid #e74c3c40;
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  width: 100%;
  transition: all 0.2s ease;
  text-decoration: none;
  color: #c0392b;
  margin-top: 0.5rem;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(192, 57, 43, 0.15);
    background: #fde8e8;
    border-color: #e74c3c80;
  }
`;

const TransferButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f0fbff;
  border: 1px solid #29a8d340;
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  width: 100%;
  transition: all 0.2s ease;
  text-decoration: none;
  color: #1e7ea3;
  margin-top: 0.5rem;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(41, 168, 211, 0.15);
    background: #e6f7ff;
    border-color: #29a8d380;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const SnapshotImage = styled.img`
  width: 100%;
  margin-top: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.primary}20;
  display: block;
`;

const SnapshotBlock = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;

const SnapshotFrame = styled.div`
  position: relative;
  width: 100%;

  & > img {
    margin-top: 0;
  }
`;

const SnapshotRefreshButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 1.1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: rgba(0, 0, 0, 0.75); }
  &:disabled { cursor: wait; opacity: 0.8; }

  .spin {
    display: inline-block;
    animation: cc-spin 0.9s linear infinite;
  }

  @keyframes cc-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SnapshotTimestamp = styled.div`
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  padding: 0.15rem 0.5rem;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.7rem;
  border-radius: 0.5rem;
`;

const OrderReplacementButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary200}, ${({ theme }) => theme.colors.primary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: 0.75rem;
  padding: 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.small};
  width: 100%;
  margin-top: 0.5rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;

  p {
    margin: 0;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}20, ${({ theme }) => theme.colors.primary}25);
    border-color: ${({ theme }) => theme.colors.primary}50;
  }
`;

const TerminatedContainer = styled.div`
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  border: 1px solid #d1d5db;
  border-radius: 0.875rem;
  padding: 1rem;
  margin-top: 0.5rem;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6b7280, #9ca3af);
  }
`;

const TerminatedBadge = styled.div`
  background: linear-gradient(135deg, #4b5563, #6b7280);
  color: white;
  padding: 0.5rem 0.875rem;
  border-radius: 1.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  flex-shrink: 0;
`;

const TerminatedBody = styled.p`
  margin: 0.5rem 0 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid #d1d5db;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #4b5563;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  width: 100%;
  height: 100%;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  letter-spacing: -0.01em;
`;

const PlanText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 0.2rem;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}10, ${({ theme }) => theme.colors.primary}20);
  border-radius: 0.375rem;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Container = styled.div`
  padding: 1rem;
  width: 100%;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white}, ${({ theme }) => theme.colors.gray}15);
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  justify-content: space-between;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: ${({ theme }) => theme.colors.primary}30;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}70);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.small};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const resumeProgressSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const ResumeProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

const ResumeProgressWheel = styled.span`
  width: 1.75rem;
  height: 1.75rem;
  flex: 0 0 auto;
  border: 3px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${resumeProgressSpin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TransferCodeInput = styled.input`
  border: 2px solid ${({ theme }) => theme.colors.gray900}20;
  border-radius: 0.5rem;
  padding: 0.65rem 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.medium};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2rem;
  text-align: center;
  max-width: 8rem;
  margin: 0 auto;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

// New combined components for unified plan and data display
const CombinedPlanContainer = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}08, ${({ theme }) => theme.colors.primary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}25;
  border-radius: 0.875rem;
  padding: 1rem;
  margin-top: 0.5rem;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}70);
  }
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.875rem;
  gap: 0.5rem;
`;

const PlanBadge = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}90);
  color: white;
  padding: 0.5rem 0.875rem;
  border-radius: 1.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
`;

const BillingInfo = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: 600;
  opacity: 0.8;
`;

const DataSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.875rem;
  border-top: 1px solid ${({ theme }) => theme.colors.primary}25;
`;

const DataLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray900};
  text-transform: uppercase;
  letter-spacing: 0.025em;
  opacity: 0.8;
`;

const DataValue = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  line-height: 1;
`;
